import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  IconButton,
  Button,
  TextField,
  Autocomplete,
  CircularProgress,
  Typography,
  FormHelperText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WorkIcon from '@mui/icons-material/Work';
import { placementActivityApi } from '../../../../../services/api/placementActivityApi';
import { useAlert } from '../../../../../contexts/AlertContext';
import RichTextEditor from '../../../../common/RichTextEditor';
import { EMPLOYEE_RETURN_TYPES, CLIENT_RETURN_TYPES } from '../../../../../constants/selectorReturnTypes';
import EmployeeSearch from '../../../../common/EmployeeSearch';
import ProjectSearch from '../../../../common/ProjectSearch';
import ClientSelector from '../../../../common/ClientSelector';
import {
  DrawerHeader,
  DrawerTitle,
  DrawerBody,
  DrawerFooter,
  SourceProjectBadge,
  SourceProjectLabel,
  CustomToggleRow,
  TypeColorDot,
} from './styles';

const EMPTY_FORM = {
  activityTypeId: '',
  statusTypeId: '',
  ownerId: '',
  comments: '',
  targetProjectId: '',
  targetProjectName: '',
  targetClientId: '',
  targetClientName: '',
};

const PlacementActivityDrawer = ({
  open,
  onClose,
  employeeId,
  activityToEdit,
  onSaved,
  prefillTarget,
}) => {
  const { showAlert } = useAlert();
  const isEdit = Boolean(activityToEdit);

  // Source FT project (resolved by backend or taken from the edit record)
  const [ftProject, setFtProject] = useState(null);
  const [ftProjectLoading, setFtProjectLoading] = useState(false);
  const [ftProjectError, setFtProjectError] = useState(null);

  // Activity types
  const [activityTypes, setActivityTypes] = useState([]);
  const [typesLoading, setTypesLoading] = useState(false);
  const [selectedType, setSelectedType] = useState(null);

  // Status types
  const [statusTypes, setStatusTypes] = useState([]);
  const [statusTypesLoading, setStatusTypesLoading] = useState(false);
  const [selectedStatusType, setSelectedStatusType] = useState(null);

  // Form state
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // "Use custom name" toggles
  const [useCustomProject, setUseCustomProject] = useState(false);
  const [useCustomClient, setUseCustomClient] = useState(false);

  // Lock target fields when opened from a sub-group header (prefillTarget present) or in edit mode
  const targetsLocked = Boolean(prefillTarget) || isEdit;
  // Lock client when a system project is selected (client is auto-populated from it)
  const clientLocked = targetsLocked || Boolean(form.targetProjectId);

  // ── Load data when drawer opens ────────────────────────────────────────────
  useEffect(() => {
    if (!open) {
      return;
    }
    loadActivityTypes();
    loadStatusTypes();

    if (isEdit) {
      populateFromEdit(activityToEdit);
    } else {
      loadFTProject();
      resetForm(prefillTarget);
    }
  }, [open]);

  const loadActivityTypes = async () => {
    try {
      setTypesLoading(true);
      const types = await placementActivityApi.getAllActivityTypes();
      setActivityTypes(types || []);
    } catch (err) {
      showAlert('Failed to load activity types', 'error', err);
    } finally {
      setTypesLoading(false);
    }
  };

  const loadStatusTypes = async () => {
    try {
      setStatusTypesLoading(true);
      const types = await placementActivityApi.getAllStatusTypes();
      setStatusTypes(types || []);
    } catch (err) {
      showAlert('Failed to load status types', 'error', err);
    } finally {
      setStatusTypesLoading(false);
    }
  };

  const loadFTProject = async () => {
    try {
      setFtProjectLoading(true);
      setFtProjectError(null);
      const proj = await placementActivityApi.getActiveFTProject(employeeId);
      setFtProject(proj);
      if (!proj) {
        setFtProjectError(
          'This individual does not have an active Full-Time project. A source project is required to log a Placement Activity.'
        );
      }
    } catch (err) {
      setFtProjectError('Failed to resolve the active FT project.');
    } finally {
      setFtProjectLoading(false);
    }
  };

  const populateFromEdit = (activity) => {
    setFtProject(activity.sourceEmployeeProject);
    setFtProjectError(null);

    const isCustomProject = !activity.targetProjectId && Boolean(activity.targetProjectName);
    const isCustomClient  = !activity.targetClientId  && Boolean(activity.targetClientName);

    setUseCustomProject(isCustomProject);
    setUseCustomClient(isCustomClient);

    setForm({
      activityTypeId:    activity.activityTypeId    || '',
      statusTypeId:      activity.statusTypeId      || '',
      ownerId:           activity.ownerId            || '',
      comments:          activity.comments           || '',
      targetProjectId:   activity.targetProjectId    || '',
      targetProjectName: activity.targetProjectName  || '',
      targetClientId:    activity.targetClientId     || '',
      targetClientName:  activity.targetClientName   || '',
    });

    setSelectedType(activity.activityType || null);
    setSelectedStatusType(activity.statusType || null);
  };

  const resetForm = (prefill = null) => {
    setForm(prefill ? {
      ...EMPTY_FORM,
      ownerId:           prefill.ownerId           || '',
      targetProjectId:   prefill.targetProjectId   || '',
      targetProjectName: prefill.targetProjectName || '',
      targetClientId:    prefill.targetClientId    || '',
      targetClientName:  prefill.targetClientName  || '',
    } : EMPTY_FORM);
    setUseCustomProject(prefill?.useCustomProject || false);
    setUseCustomClient(prefill?.useCustomClient   || false);
    setSelectedType(null);
    setSelectedStatusType(null);
  };

  // ── Field handlers ─────────────────────────────────────────────────────────

  const handleTypeChange = (_e, value) => {
    setSelectedType(value);
    setForm(f => ({ ...f, activityTypeId: value?.id || '' }));
  };

  const handleStatusTypeChange = (_e, value) => {
    setSelectedStatusType(value);
    setForm(f => ({ ...f, statusTypeId: value?.id || '' }));
  };

  const handleClearCustomProject = () => {
    setUseCustomProject(false);
    setForm(f => ({ ...f, targetProjectName: '', targetProjectId: '' }));
  };

  const handleClearCustomClient = () => {
    setUseCustomClient(false);
    setForm(f => ({ ...f, targetClientName: '', targetClientId: '' }));
  };

  // ── Submit ─────────────────────────────────────────────────────────────────

  const isValid = () => {
    if (!form.activityTypeId) { return false; }
    if (!form.ownerId)        { return false; }
    return true;
  };

  const handleSave = async () => {
    if (!isValid()) { return; }
    try {
      setSaving(true);
      const payload = {
        activityTypeId:    form.activityTypeId,
        statusTypeId:      form.statusTypeId || null,
        ownerId:           form.ownerId,
        comments:          form.comments || null,
        targetProjectId:   useCustomProject ? null : (form.targetProjectId || null),
        targetProjectName: useCustomProject ? (form.targetProjectName || null) : null,
        targetClientId:    useCustomClient  ? null : (form.targetClientId  || null),
        targetClientName:  useCustomClient  ? (form.targetClientName  || null) : null,
      };

      if (isEdit) {
        await placementActivityApi.updateActivity(activityToEdit.id, payload);
        showAlert('Placement activity updated successfully', 'success');
      } else {
        await placementActivityApi.createActivity(employeeId, payload);
        showAlert('Placement activity created successfully', 'success');
      }

      onSaved();
      onClose();
    } catch (err) {
      showAlert(err.message || `Failed to ${isEdit ? 'update' : 'create'} placement activity`, 'error', err);
    } finally {
      setSaving(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 480, display: 'flex', flexDirection: 'column' } }}
    >
      <DrawerHeader>
        <DrawerTitle>{isEdit ? 'Edit Placement Activity' : 'Add Placement Activity'}</DrawerTitle>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DrawerHeader>

      <DrawerBody>
        {/* Source project (read-only) */}
        <Box>
          <SourceProjectLabel>Current Project</SourceProjectLabel>
          {ftProjectLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">Resolving FT project…</Typography>
            </Box>
          ) : ftProjectError ? (
            <FormHelperText error sx={{ fontSize: '13px', mt: 0 }}>{ftProjectError}</FormHelperText>
          ) : ftProject ? (
            <SourceProjectBadge>
              <WorkIcon sx={{ fontSize: 18, color: '#64748B' }} />
              <span>{ftProject.project?.name || ftProject.name || '—'}</span>
            </SourceProjectBadge>
          ) : null}
        </Box>

        {/* Activity Type */}
        <Autocomplete
          options={activityTypes}
          getOptionLabel={(o) => o.name || ''}
          value={selectedType}
          onChange={handleTypeChange}
          loading={typesLoading}
          isOptionEqualToValue={(o, v) => o.id === v?.id}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TypeColorDot $color={option.color} />
                {option.name}
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Activity Type *"
              variant="outlined"
              size="small"
              InputProps={{
                ...params.InputProps,
                startAdornment: selectedType ? (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', ml: '4px', mr: '2px' }}>
                      <TypeColorDot $color={selectedType.color} />
                    </Box>
                    {params.InputProps.startAdornment}
                  </>
                ) : params.InputProps.startAdornment,
              }}
              error={!form.activityTypeId && saving}
              helperText={!form.activityTypeId && saving ? 'Activity type is required' : ''}
            />
          )}
        />

        {/* Status Type */}
        <Autocomplete
          options={statusTypes}
          getOptionLabel={(o) => o.name || ''}
          value={selectedStatusType}
          onChange={handleStatusTypeChange}
          loading={statusTypesLoading}
          isOptionEqualToValue={(o, v) => o.id === v?.id}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TypeColorDot $color={option.color} />
                {option.name}
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Status Type (optional)"
              variant="outlined"
              size="small"
              InputProps={{
                ...params.InputProps,
                startAdornment: selectedStatusType ? (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', ml: '4px', mr: '2px' }}>
                      <TypeColorDot $color={selectedStatusType.color} />
                    </Box>
                    {params.InputProps.startAdornment}
                  </>
                ) : params.InputProps.startAdornment,
              }}
            />
          )}
        />

        {/* Owner */}
        <Box>
          <EmployeeSearch
            value={form.ownerId || null}
            onChange={(val) => setForm(f => ({ ...f, ownerId: val || '' }))}
            returnValue={EMPLOYEE_RETURN_TYPES.ID}
            label="Owner *"
            placeholder="Search individual..."
            width="100%"
            sx={{ width: '100%' }}
            showSelectedAvatar
          />
          {!form.ownerId && saving && (
            <FormHelperText error>Owner is required</FormHelperText>
          )}
        </Box>

        {/* Target Project */}
        {useCustomProject ? (
          <Box>
            <TextField
              fullWidth
              size="small"
              label="Custom Target Project Name"
              value={form.targetProjectName}
              onChange={(e) => setForm(f => ({ ...f, targetProjectName: e.target.value }))}
              variant="outlined"
              disabled={targetsLocked}
            />
            {!targetsLocked && (
              <CustomToggleRow>
                <Button size="small" onClick={handleClearCustomProject} sx={{ fontSize: '12px', color: '#64748B' }}>
                  ← Select from system projects
                </Button>
              </CustomToggleRow>
            )}
          </Box>
        ) : (
          <Box>
            <ProjectSearch
              value={form.targetProjectId || null}
              onChange={(project) => {
                if (!project) {
                  setForm(f => ({ ...f, targetProjectId: '', targetProjectName: '' }));
                  return;
                }
                setForm(f => ({
                  ...f,
                  targetProjectId: project.id,
                  targetProjectName: '',
                  // Auto-populate client if the project has one and no client is set yet
                  ...(project.client && !f.targetClientId && !f.targetClientName
                    ? { targetClientId: project.client.id, targetClientName: '' }
                    : {}),
                }));
              }}
              returnValue={null}
              label="Target Project (optional)"
              placeholder="Search target project..."
              sx={{ width: '100%' }}
              width="100%"
              disabled={targetsLocked}
            />
            {!targetsLocked && (
              <CustomToggleRow>
                <Button size="small" onClick={() => setUseCustomProject(true)} sx={{ fontSize: '12px', color: '#64748B' }}>
                  + Enter custom project name
                </Button>
              </CustomToggleRow>
            )}
          </Box>
        )}

        {/* Target Client */}
        {useCustomClient ? (
          <Box>
            <TextField
              fullWidth
              size="small"
              label="Custom Target Client Name"
              value={form.targetClientName}
              onChange={(e) => setForm(f => ({ ...f, targetClientName: e.target.value }))}
              variant="outlined"
              disabled={clientLocked}
            />
            {!clientLocked && (
              <CustomToggleRow>
                <Button size="small" onClick={handleClearCustomClient} sx={{ fontSize: '12px', color: '#64748B' }}>
                  ← Select from system clients
                </Button>
              </CustomToggleRow>
            )}
          </Box>
        ) : (
          <Box>
            <ClientSelector
              value={form.targetClientId || ''}
              onChange={(val) => setForm(f => ({ ...f, targetClientId: val || '', targetClientName: '' }))}
              returnValue={CLIENT_RETURN_TYPES.ID}
              label="Target Client (optional)"
              enableClientCreation={false}
              fullWidth
              size="small"
              disabled={clientLocked}
            />
            {!clientLocked && (
              <CustomToggleRow>
                <Button size="small" onClick={() => setUseCustomClient(true)} sx={{ fontSize: '12px', color: '#64748B' }}>
                  + Enter custom client name
                </Button>
              </CustomToggleRow>
            )}
          </Box>
        )}

        {/* Comments */}
        <Box>
          <Typography variant="caption" sx={{ color: '#64748B', mb: 0.5, display: 'block' }}>
            Comments
          </Typography>
          <RichTextEditor
            variant="compact"
            value={form.comments}
            onChange={(html) => setForm(f => ({ ...f, comments: html }))}
            minHeight={120}
          />
        </Box>
      </DrawerBody>

      <DrawerFooter>
        <Button variant="outlined" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !isValid() || !!ftProjectError || ftProjectLoading}
        >
          {saving ? <CircularProgress size={18} /> : (isEdit ? 'Update' : 'Create')}
        </Button>
      </DrawerFooter>
    </Drawer>
  );
};

export default PlacementActivityDrawer;
